/**
 * DashboardPage Component
 * 
 * Main user progress dashboard showing drill completion,
 * learning stages, and resume functionality
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { getProgressSummary, type DashboardProgressSummary } from '@/services/dashboard-progress';
import { ResumeCard } from '@/components/dashboard/ResumeCard';
import { ModuleSection } from '@/components/dashboard/ModuleSection';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, TrendingUp, Target, Award } from 'lucide-react';

export function DashboardPage() {
  const [summary, setSummary] = useState<DashboardProgressSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  async function loadDashboard() {
    setIsLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const data = await getProgressSummary(user.id);
      setSummary(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load dashboard');
      setError(error);
      console.error('Dashboard load error:', error);
    } finally {
      setIsLoading(false);
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <Skeleton className="h-10 w-64 mb-8" />
        <Skeleton className="h-32 w-full mb-6" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 flex items-center gap-4">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-red-900 mb-1">
                Failed to Load Dashboard
              </h2>
              <p className="text-sm text-red-700 mb-3">{error.message}</p>
              <Button onClick={loadDashboard} variant="outline" size="sm">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Empty state
  if (!summary || summary.overall_stats.total_drills === 0) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Your Progress</h1>
        <Card>
          <CardContent className="p-12 text-center">
            <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Ready to Start Your Journey?
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Begin your golf improvement with research-backed drills tailored to your learning stage.
            </p>
            <Link to="/drills">
              <Button size="lg">Browse Drills</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main dashboard
  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Progress</h1>
        <p className="text-gray-600">
          Track your drill completion and learning progression
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Overall Progress</p>
                <p className="text-3xl font-bold text-gray-900">
                  {summary.overall_stats.overall_completion_percentage}%
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-600" aria-hidden="true" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Drills Completed</p>
                <p className="text-3xl font-bold text-gray-900">
                  {summary.overall_stats.completed_drills}
                  <span className="text-lg text-gray-500 ml-1">
                    / {summary.overall_stats.total_drills}
                  </span>
                </p>
              </div>
              <Award className="w-8 h-8 text-green-600" aria-hidden="true" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">In Progress</p>
                <p className="text-3xl font-bold text-gray-900">
                  {summary.overall_stats.in_progress_drills}
                </p>
              </div>
              <Target className="w-8 h-8 text-orange-600" aria-hidden="true" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resume drill card */}
      {summary.resume_drill && <ResumeCard drill={summary.resume_drill} />}

      {/* Module sections */}
      {summary.modules.map(module => (
        <ModuleSection key={module.module_id} module={module} />
      ))}
    </div>
  );
}

